import { css } from '@emotion/react';
import { faShare } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LinkComponent } from '~/_shared/baseComponents/links';
import { usePointerClickEvent } from '~/_shared/baseComponents/utils/usePointerClickEvent';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { useTranslation } from '~/_shared/utils/hooks';
import { convertNumberToLetters } from '~/_shared/utils/text/text.helpers';
import {
  getMapRowFlags, getSubMapsCount,
} from '~/map/listing/mapListing.helpers';
import type { MapListingRowItem } from '~/map/listing/tableData/mapRow/mapRow.component';
import { getMapUrl } from '~/map/map.helpers';
import { type ShareMapProps } from '~/map/share/shareMap.container';
import { getMapRowIcon } from '../../tableData/mapRow/mapRow.helpers';
import { MobileMapRowToggleComponent } from './mobileMapRowToggle.component';

const tableRowStyle = ({ theme }: ThemeProps) => css({
  height: 64,
  background: theme.backgroundColors.quinary,
  borderBottom: `1px solid ${theme.borderColors.primary}`,
});

const tableDataStyle = css({
  padding: '0 10px',
});

const subMapTableDataStyle = css({
  fontSize: 16,
  fontWeight: 700,
  paddingLeft: 40,
});

const linkStyle = ({ theme }: ThemeProps) => css({
  color: theme.textColors.primary,
  fontSize: 16,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
});

const linkIconStyle = css({
  marginRight: 15,
});

const subMapsCountStyle = ({ theme }: ThemeProps) => css({
  background: theme.buttonColors.primaryBackground,
  borderRadius: 12,
  color: theme.buttonColors.primaryText,
  display: 'block',
  fontSize: 14,
  height: 24,
  lineHeight: '24px',
  marginRight: 40,
  textAlign: 'center',
  width: 40,
});

const shareButtonStyle = ({ theme }: ThemeProps) => css({
  border: 'none',
  background: 'none',
  fontSize: 30,
  padding: 5,
  color: theme.buttonColors.quaternaryText,
  display: 'inline-block',
});

const toggleColumnStyle = css({
  width: 40,
  padding: '0 10px',
});

const shareColumnStyle = css({
  width: 45,
  textAlign: 'right',
  paddingRight: 10,
});

const counterStyle = css({
  display: 'inline-block',
  marginRight: 8,
  minWidth: 22,
});

type MobileMapRowProps = Readonly<{
  areSubMapsExpanded: boolean;
  index: number;
  item: MapListingRowItem;
  parentMapItem?: MapListingRowItem;

  onMapShareSettingsClick: (props: ShareMapProps) => void;
  onShowSubMapsToggle?: () => void;
  setRef?: (ref: HTMLTableRowElement) => void;
}>;

export const MobileMapRowComponent: FC<MobileMapRowProps> = (props) => {
  const theme = useTheme();
  const [t] = useTranslation();

  const subMapsCount = getSubMapsCount(props.item);
  const { isSnapshot, isSubItem, canMapBeShared, userHasAccess } = getMapRowFlags(props.item, props.parentMapItem);

  const { onClick, onPointerDown, onPointerUp } = usePointerClickEvent(() => {
    if (!canMapBeShared || !userHasAccess) {
      return;
    }

    props.onMapShareSettingsClick({
      initialShareId: props.item.shareId ?? undefined,
      mapId: props.item.id,
      mapName: props.item.name,
      mapPrivacyLevel: props.item.privacy,
      isSnapshot,
      parentMapPrivacy: props.parentMapItem?.privacy || null,
      mapSource: props.item.source,
    });
  });

  return (
    <tr
      css={tableRowStyle({ theme })}
      ref={props?.setRef ? props.setRef : null}
    >
      <td css={tableDataStyle}>
        <div css={isSubItem ? subMapTableDataStyle : undefined}>
          <LinkComponent
            path={userHasAccess ? getMapUrl(props.item.id) : null}
            css={linkStyle({ theme })}
            tooltip={userHasAccess ? props.item.name : t('map.noAccess')}
            data-testid={`navigate-map-${props.item.name}`}
            disabled={!userHasAccess}
          >
            <span css={counterStyle}>{isSubItem ? convertNumberToLetters(props.index + 1) : props.index + 1}.</span>
            <FontAwesomeIcon
              css={linkIconStyle}
              icon={getMapRowIcon({ isSnapshot, item: props.item })}
            />
            {props.item.name}
          </LinkComponent>
        </div>
      </td>

      <td css={toggleColumnStyle}>
        {!isSubItem && subMapsCount > 0 && (
          <MobileMapRowToggleComponent
            areSubMapsExpanded={props.areSubMapsExpanded}
            item={props.item}
            onClick={props.onShowSubMapsToggle}
          >
            <span css={subMapsCountStyle({ theme })}>
              {subMapsCount}
            </span>
          </MobileMapRowToggleComponent>
        )}
      </td>

      <td css={shareColumnStyle}>
        {userHasAccess && canMapBeShared && (
          <button
            css={shareButtonStyle({ theme })}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
          >
            <FontAwesomeIcon icon={faShare} />
          </button>
        )}
      </td>
    </tr>
  );
};
